import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
import {
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Description as FileIcon,
} from "@mui/icons-material";
import {
  useLazyExportLoadShiftingCSVQuery,
  useLazyExportPeakShavingCSVQuery,
  useLazyExportPublicHolidaysCSVQuery,
  useLazyExportTOUDataCSVQuery,
  useLazyExportTariffDataCSVQuery,
  useValidateLoadShiftingCSVMutation,
  useValidatePeakShavingCSVMutation,
  useValidatePublicHolidaysCSVMutation,
  useValidateTOUDataCSVMutation,
  useValidateTariffDataCSVMutation,
  useImportLoadShiftingCSVMutation,
  useImportPeakShavingCSVMutation,
  useImportPublicHolidaysCSVMutation,
  useImportTOUDataCSVMutation,
  useImportTariffDataCSVMutation,
} from "./csvApiSlice";

const CSV_TYPES = {
  'load-shifting': {
    title: 'Load Shifting Schedules',
    exportHook: useLazyExportLoadShiftingCSVQuery,
    validateHook: useValidateLoadShiftingCSVMutation,
    importHook: useImportLoadShiftingCSVMutation,
  },
  'peak-shaving': {
    title: 'Peak Shaving Schedules',
    exportHook: useLazyExportPeakShavingCSVQuery,
    validateHook: useValidatePeakShavingCSVMutation,
    importHook: useImportPeakShavingCSVMutation,
  },
  'public-holidays': {
    title: 'Public Holidays',
    exportHook: useLazyExportPublicHolidaysCSVQuery,
    validateHook: useValidatePublicHolidaysCSVMutation,
    importHook: useImportPublicHolidaysCSVMutation,
  },
  'tou-data': {
    title: 'Time of Use Data',
    exportHook: useLazyExportTOUDataCSVQuery,
    validateHook: useValidateTOUDataCSVMutation,
    importHook: useImportTOUDataCSVMutation,
  },
  'tariff-data': {
    title: 'Tariff Data',
    exportHook: useLazyExportTariffDataCSVQuery,
    validateHook: useValidateTariffDataCSVMutation,
    importHook: useImportTariffDataCSVMutation,
  },
};

const steps = [
  {
    label: 'Download Current Data',
    description: 'Download the existing data as a CSV file to edit',
  },
  {
    label: 'Upload & Save',
    description: 'Upload your modified CSV file, validate, and save changes',
  },
];

export default function CSVBulkUpdate({ open, onClose, csvType, onSuccess }) {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const csvConfig = CSV_TYPES[csvType];
  
  // Hooks
  const [triggerExport, { isLoading: isExporting }] = csvConfig.exportHook();
  const [validateCSV, { isLoading: isValidating }] = csvConfig.validateHook();
  const [importCSV, { isLoading: isImporting }] = csvConfig.importHook();

  const handleClose = () => {
    setActiveStep(0);
    setUploadedFile(null);
    setValidationResult(null);
    setIsDownloaded(false);
    onClose();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleDownload = async () => {
    try {
      await triggerExport();
      setIsDownloaded(true);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadedFile(file);
      setValidationResult(null);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleValidation = async () => {
    if (!uploadedFile) return;

    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const result = await validateCSV(formData).unwrap();
      setValidationResult(result);
      // Don't automatically move to next step - show buttons in current step
    } catch (error) {
      setValidationResult({ 
        valid: false, 
        errors: ['Validation failed. Please check your file format.'] 
      });
    }
  };

  const handleSaveAndClose = async () => {
    if (!validationResult?.data) return;

    try {
      const result = await importCSV(validationResult.data).unwrap();
      // Always call onSuccess if we got a result
      onSuccess?.(result);
      // Always close the modal after import completes
      handleClose();
    } catch (error) {
      console.error('Import failed:', error);
      // Still close the modal even on error
      // (error message would have been shown to user already)
      handleClose();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadStep 
            onDownload={handleDownload}
            isLoading={isExporting}
            isDownloaded={isDownloaded}
            csvType={csvType}
          />
        );
      case 1:
        return (
          <UploadStep
            onFileUpload={handleFileUpload}
            uploadedFile={uploadedFile}
            onValidate={handleValidation}
            isValidating={isValidating}
            validationResult={validationResult}
            onSaveAndClose={handleSaveAndClose}
            onCancel={handleClose}
            isImporting={isImporting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            CSV Bulk Update - {csvConfig?.title}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="h6">{step.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  {renderStepContent(index)}
                </Box>
                {index === 0 && (
                  <Box sx={{ mb: 1 }}>
                    <div>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isDownloaded || isExporting}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Continue
                      </Button>
                    </div>
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

function DownloadStep({ onDownload, isLoading, isDownloaded, csvType }) {
  return (
    <Paper sx={{ p: 3, textAlign: 'center' }}>
      <DownloadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Download Current Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Download the existing {CSV_TYPES[csvType]?.title.toLowerCase()} as a CSV file.
        You can then modify this file in Excel or any spreadsheet application.
      </Typography>

      {isDownloaded ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Download Complete</AlertTitle>
          Your CSV file has been downloaded. You can now proceed to the next step.
        </Alert>
      ) : (
        <Button
          variant="contained"
          size="large"
          onClick={onDownload}
          disabled={isLoading}
          startIcon={<DownloadIcon />}
        >
          {isLoading ? 'Downloading...' : 'Download CSV'}
        </Button>
      )}

      {isLoading && <LinearProgress sx={{ mt: 2 }} />}
    </Paper>
  );
}

function UploadStep({ onFileUpload, uploadedFile, onValidate, isValidating, validationResult, onSaveAndClose, onCancel, isImporting }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <UploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Upload Modified CSV
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select your modified CSV file to upload and validate
        </Typography>
      </Box>

      <input
        accept=".csv"
        style={{ display: 'none' }}
        id="csv-upload-button"
        type="file"
        onChange={onFileUpload}
      />
      <label htmlFor="csv-upload-button">
        <Button
          variant="outlined"
          component="span"
          fullWidth
          size="large"
          startIcon={<UploadIcon />}
          sx={{ mb: 2 }}
        >
          Select CSV File
        </Button>
      </label>

      {uploadedFile && (
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<FileIcon />}
            label={uploadedFile.name}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      <Button
        variant="contained"
        onClick={onValidate}
        disabled={!uploadedFile || isValidating}
        fullWidth
        size="large"
      >
        {isValidating ? 'Validating...' : 'Validate CSV'}
      </Button>

      {isValidating && <LinearProgress sx={{ mt: 2 }} />}

      {validationResult && (
        <Box sx={{ mt: 3 }}>
          {validationResult.valid ? (
            <>
              <Alert severity="success">
                <AlertTitle>Validation Successful</AlertTitle>
                All {validationResult.total_rows} rows are valid and ready to import.
              </Alert>
              
            </>
          ) : (
            <Alert severity="error">
              <AlertTitle>Validation Failed</AlertTitle>
              {validationResult.errors?.length > 0 && (
                <List dense>
                  {validationResult.errors.slice(0, 10).map((error, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText 
                        primary={error}
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      />
                    </ListItem>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <ListItem sx={{ pl: 0 }}>
                      <ListItemText 
                        primary={`... and ${validationResult.errors.length - 10} more errors`}
                        sx={{ '& .MuiListItemText-primary': { 
                          fontSize: '0.875rem', 
                          fontStyle: 'italic' 
                        } }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Alert>
          )}
        </Box>
      )}

      {validationResult?.valid && (
        <>
          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {validationResult?.changes?.creates || 0}
                </Typography>
                <Typography variant="caption">To Create</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">
                  {validationResult?.changes?.updates || 0}
                </Typography>
                <Typography variant="caption">To Update</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {validationResult?.changes?.deletes || 0}
                </Typography>
                <Typography variant="caption">To Delete</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  {validationResult?.total_rows || 0}
                </Typography>
                <Typography variant="caption">Total Rows</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Import Information</AlertTitle>
            • New records (without ID) will be created<br/>
            • Existing records (with ID) will be updated<br/>
            • Records not in CSV will be deleted<br/>
            • Changes will be applied immediately and cannot be undone
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={onSaveAndClose}
              disabled={isImporting}
              size="large"
              color="primary"
            >
              {isImporting ? 'Saving...' : 'Save & Close'}
            </Button>
          </Box>

          {isImporting && <LinearProgress sx={{ mt: 2 }} />}
        </>
      )}
    </Paper>
  );
}

